import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertCircle, ChevronLeft, ChevronRight, ClipboardList, Clock, Edit2, FileImage, FileText, Receipt, Eye, X, Fuel } from "lucide-react";
import { Badge, Button, Card, Spinner } from "../shared/ui";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyRecordsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [timesheets, setTimesheets] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [profile, setProfile] = useState(null);
  const [weeklyTimes, setWeeklyTimes] = useState(null);
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    return new Date(today.setDate(diff)).toISOString().split('T')[0];
  });
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [showAlterationModal, setShowAlterationModal] = useState(false);
  const [selectedTimeEntry, setSelectedTimeEntry] = useState(null);
  const [alterationForm, setAlterationForm] = useState({ reason: '', signature_data: '' });
  const [signatureRef, setSignatureRef] = useState(null);
  const [saving, setSaving] = useState(false);
  const [myReceipts, setMyReceipts] = useState(null);
  const [previewReceipt, setPreviewReceipt] = useState(null);
  const [receiptFilter, setReceiptFilter] = useState("all"); // all | needs_reimb | reimbursed

  useEffect(() => {
    fetchData();
  }, [activeTab, weekStart]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === "overview") {
        const res = await axios.get(`${API}/my-records/summary`);
        setSummary(res.data);
      } else if (activeTab === "timesheets") {
        const res = await axios.get(`${API}/timesheets`);
        setTimesheets(res.data);
      } else if (activeTab === "payslips") {
        const res = await axios.get(`${API}/payslips`);
        setPayslips(res.data);
      } else if (activeTab === "documents") {
        const res = await axios.get(`${API}/document-submissions`);
        setDocuments(res.data);
      } else if (activeTab === "profile") {
        const res = await axios.get(`${API}/users/${user.id}/profile`);
        setProfile(res.data);
        setProfileForm(res.data);
      } else if (activeTab === "weekly") {
        const [wk, rcp] = await Promise.all([
          axios.get(`${API}/my-records/weekly-times?week_start=${weekStart}`),
          axios.get(`${API}/my-records/receipts?start_date=${weekStart}&end_date=${new Date(new Date(weekStart).getTime() + 6*86400000).toISOString().split('T')[0]}`),
        ]);
        setWeeklyTimes({ ...wk.data, _receipts: rcp.data });
      } else if (activeTab === "receipts") {
        const res = await axios.get(`${API}/my-records/receipts`);
        setMyReceipts(res.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/${user.id}/profile`, {
        date_of_birth: profileForm.date_of_birth,
        gender: profileForm.gender,
        address_line_1: profileForm.address_line_1,
        address_line_2: profileForm.address_line_2,
        suburb: profileForm.suburb,
        state: profileForm.state,
        postcode: profileForm.postcode,
        country: profileForm.country,
        tax_file_number: profileForm.tax_file_number,
        employment_type: profileForm.employment_type,
        start_date: profileForm.start_date,
        position_title: profileForm.position_title,
        drivers_license_number: profileForm.drivers_license_number,
        drivers_license_class: profileForm.drivers_license_class,
        drivers_license_expiry: profileForm.drivers_license_expiry,
        medical_conditions: profileForm.medical_conditions,
        emergency_contact: profileForm.emergency_contact || {},
        superannuation: profileForm.superannuation || {},
        bank_details: profileForm.bank_details || {},
      });
      // Capabilities live on the user doc itself — separate endpoint.
      if (Array.isArray(profileForm.capabilities)) {
        await axios.put(`${API}/users/${user.id}/capabilities`, { capabilities: profileForm.capabilities });
      }
      toast.success("Profile updated!");
      setEditingProfile(false);
      fetchData();
    } catch (e) {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const submitTimeAlteration = async () => {
    if (!alterationForm.reason) {
      toast.error("Please provide a reason for the change");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/time-alterations`, {
        time_entry_id: selectedTimeEntry.id,
        original_clock_in: selectedTimeEntry.clock_in,
        original_clock_out: selectedTimeEntry.clock_out,
        requested_clock_in: alterationForm.requested_clock_in || selectedTimeEntry.clock_in,
        requested_clock_out: alterationForm.requested_clock_out || selectedTimeEntry.clock_out,
        requested_job_id: alterationForm.requested_job_id,
        reason: alterationForm.reason,
        signature_data: alterationForm.signature_data || "" // Signature now optional
      });
      toast.success("Time change submitted! Your supervisor will see this on your timesheet.");
      setShowAlterationModal(false);
      setSelectedTimeEntry(null);
      setAlterationForm({ reason: '', signature_data: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit request");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-AU') : '-';
  const formatTime = (dateStr) => dateStr ? new Date(dateStr).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : '-';
  const formatHours = (hours) => {
    if (!hours) return "0h";
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const changeWeek = (direction) => {
    const current = new Date(weekStart);
    current.setDate(current.getDate() + (direction * 7));
    setWeekStart(current.toISOString().split('T')[0]);
  };

  const updateProfileField = (field, value) => {
    setProfileForm(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (parent, field, value) => {
    setProfileForm(prev => ({
      ...prev,
      [parent]: { ...(prev[parent] || {}), [field]: value }
    }));
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-records-page">
      <div>
        <h1 className="text-3xl font-bold uppercase tracking-tight font-heading">My Records</h1>
        <p className="text-slate-600">Manage your profile, view timesheets, payslips, and documents</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {['overview', 'profile', 'weekly', 'timesheets', 'payslips', 'receipts', 'documents'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            data-testid={`tab-${tab}`}
            className={`px-4 py-2 rounded-md font-semibold text-sm uppercase transition-colors ${
              activeTab === tab ? 'bg-orange-500 text-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {tab === 'weekly' ? 'Weekly Times' : tab}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Spinner size="lg" /></div>
      ) : (
        <>
          {/* Overview Tab */}
          {activeTab === "overview" && summary && (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="text-center">
                <ClipboardList className="w-10 h-10 mx-auto mb-2 text-orange-500" />
                <p className="text-3xl font-bold">{summary.timesheets.total}</p>
                <p className="text-sm text-slate-600">Total Timesheets</p>
                <p className="text-xs text-green-600">{summary.timesheets.approved} approved</p>
                {summary.timesheets.pending > 0 && (
                  <p className="text-xs text-amber-600">{summary.timesheets.pending} pending</p>
                )}
              </Card>
              <Card className="text-center">
                <FileText className="w-10 h-10 mx-auto mb-2 text-green-500" />
                <p className="text-3xl font-bold">{summary.payslips.total}</p>
                <p className="text-sm text-slate-600">Payslips</p>
              </Card>
              <Card className="text-center">
                <FileImage className="w-10 h-10 mx-auto mb-2 text-blue-500" />
                <p className="text-3xl font-bold">{summary.documents.total}</p>
                <p className="text-sm text-slate-600">Documents</p>
              </Card>
              <Card className="text-center">
                <FileText className="w-10 h-10 mx-auto mb-2 text-purple-500" />
                <p className="text-3xl font-bold">{summary.forms.total}</p>
                <p className="text-sm text-slate-600">Forms Submitted</p>
              </Card>

              {/* Recent Activity */}
              <div className="md:col-span-2">
                <Card>
                  <h3 className="font-bold uppercase mb-4">Recent Timesheets</h3>
                  {summary.timesheets.recent?.length > 0 ? (
                    <div className="space-y-2">
                      {summary.timesheets.recent.map(ts => (
                        <div key={ts.id} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                          <div>
                            <p className="font-medium">Week of {formatDate(ts.week_start)}</p>
                            <p className="text-sm text-slate-500">{formatHours(ts.total_submitted_hours)}</p>
                          </div>
                          <Badge variant={ts.status === 'approved' ? 'success' : ts.status === 'rejected' ? 'error' : 'warning'}>
                            {ts.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">No timesheets yet</p>
                  )}
                </Card>
              </div>

              <div className="md:col-span-2">
                <Card>
                  <h3 className="font-bold uppercase mb-4">Recent Payslips</h3>
                  {summary.payslips.recent?.length > 0 ? (
                    <div className="space-y-2">
                      {summary.payslips.recent.map(ps => (
                        <div key={ps.id} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                          <div>
                            <p className="font-medium">{formatDate(ps.pay_period_start)} - {formatDate(ps.pay_period_end)}</p>
                            <p className="text-sm text-slate-500">Uploaded {formatDate(ps.uploaded_at)}</p>
                          </div>
                          <Button size="sm" variant="outline">View</Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">No payslips yet</p>
                  )}
                </Card>
              </div>
            </div>
          )}

          {/* Timesheets Tab */}
          {activeTab === "timesheets" && (
            <div className="space-y-4">
              {timesheets.length > 0 ? (
                timesheets.map(ts => (
                  <Card key={ts.id}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-bold">Week of {formatDate(ts.week_start)}</h3>
                        <p className="text-sm text-slate-600">
                          {formatHours(ts.total_submitted_hours)} • {ts.entries?.length || 0} entries
                          {ts.laa_days > 0 && <span className="text-blue-600 ml-2">• {ts.laa_days} LAA days</span>}
                        </p>
                        {ts.reimbursements?.length > 0 && (
                          <p className="text-sm text-green-600">${ts.total_reimbursements?.toFixed(2)} in reimbursements</p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">Submitted {formatDate(ts.submitted_at)}</p>
                        {ts.assigned_to_name && <p className="text-xs text-blue-600">Assigned to: {ts.assigned_to_name}</p>}
                        {ts.approved_by_name && <p className="text-xs text-green-600">Approved by: {ts.approved_by_name}</p>}
                      </div>
                      <Badge variant={ts.status === 'approved' ? 'success' : ts.status === 'rejected' ? 'error' : 'warning'}>
                        {ts.status}
                      </Badge>
                    </div>
                    {ts.approval_notes && (
                      <p className="mt-2 text-sm bg-slate-50 p-2 rounded">{ts.approval_notes}</p>
                    )}
                  </Card>
                ))
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <ClipboardList className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No timesheets found</p>
                </div>
              )}
            </div>
          )}

          {/* Payslips Tab */}
          {activeTab === "payslips" && (
            <div className="space-y-4">
              {payslips.length > 0 ? (
                payslips.map(ps => (
                  <Card key={ps.id}>
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-bold">Pay Period: {formatDate(ps.pay_period_start)} - {formatDate(ps.pay_period_end)}</h3>
                        <p className="text-sm text-slate-500">
                          {ps.file_name} • Uploaded {formatDate(ps.uploaded_at)}
                        </p>
                        {ps.notes && <p className="text-sm text-slate-600 mt-1">{ps.notes}</p>}
                      </div>
                      <a 
                        href={ps.file_url} 
                        download={ps.file_name}
                        className="px-4 py-2 bg-orange-500 text-white rounded font-semibold text-sm hover:bg-orange-600 transition-colors"
                      >
                        Download
                      </a>
                    </div>
                  </Card>
                ))
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No payslips available</p>
                </div>
              )}
            </div>
          )}

          {/* Receipts Tab — fuel stops + reimbursements + daybook receipts the worker logged */}
          {activeTab === "receipts" && (
            <div className="space-y-4" data-testid="my-receipts-tab">
              {!myReceipts || myReceipts.total === 0 ? (
                <Card className="text-center py-12 text-slate-500">
                  <Receipt className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-lg font-semibold">No receipts yet</p>
                  <p className="text-sm">Fuel stops, reimbursements and daybook receipts you log will appear here</p>
                </Card>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Card className="text-center p-3">
                      <p className="text-2xl font-bold">{myReceipts.total}</p>
                      <p className="text-[10px] text-slate-500 uppercase font-bold">My Receipts</p>
                    </Card>
                    <Card className="text-center p-3">
                      <p className="text-2xl font-bold text-green-600">${(myReceipts.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      <p className="text-[10px] text-slate-500 uppercase font-bold">Total Amount</p>
                    </Card>
                    <Card className="text-center p-3 border-2 border-amber-300 bg-amber-50" data-testid="needs-reimb-card">
                      <p className="text-2xl font-bold text-amber-700">${(myReceipts.needs_reimbursement_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      <p className="text-[10px] text-amber-800 uppercase font-bold">Owed to You</p>
                      <p className="text-[10px] text-amber-700">{myReceipts.needs_reimbursement_count || 0} pending</p>
                    </Card>
                    <Card className="text-center p-3">
                      <p className="text-lg font-bold">{myReceipts.with_photo} <span className="text-amber-600 text-sm font-semibold">/ {myReceipts.without_photo} no photo</span></p>
                      <p className="text-[10px] text-slate-500 uppercase font-bold">With / Without Photo</p>
                    </Card>
                  </div>

                  {/* Filter tabs */}
                  <div className="flex gap-2 flex-wrap" data-testid="receipt-filter-tabs">
                    {[
                      { k: 'all', label: 'All' },
                      { k: 'needs_reimb', label: `Needs Reimbursement (${myReceipts.needs_reimbursement_count || 0})` },
                      { k: 'reimbursed', label: 'Already Reimbursed' }
                    ].map(t => (
                      <button
                        key={t.k}
                        onClick={() => setReceiptFilter(t.k)}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold border ${receiptFilter === t.k ? 'bg-orange-500 text-slate-900 border-orange-500' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                        data-testid={`receipt-filter-${t.k}`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm bg-white rounded-lg border">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-2 font-bold">Date</th>
                          <th className="text-left p-2 font-bold">Source</th>
                          <th className="text-left p-2 font-bold">Type</th>
                          <th className="text-right p-2 font-bold">Amount</th>
                          <th className="text-left p-2 font-bold">Description</th>
                          <th className="text-left p-2 font-bold">Job / Trip</th>
                          <th className="text-left p-2 font-bold">Reimburse?</th>
                          <th className="p-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {myReceipts.receipts
                          .filter(r => {
                            if (receiptFilter === 'needs_reimb') return r.needs_reimbursement && r.reimbursement_status !== 'reimbursed';
                            if (receiptFilter === 'reimbursed') return r.reimbursement_status === 'reimbursed';
                            return true;
                          })
                          .map(r => (
                          <tr key={`${r.source}-${r.id}`} className={`border-t border-slate-100 hover:bg-slate-50 ${r.needs_reimbursement && r.reimbursement_status !== 'reimbursed' ? 'bg-amber-50/40' : ''}`} data-testid={`my-receipt-row-${r.id}`}>
                            <td className="p-2 text-xs">{r.date ? new Date(r.date).toLocaleDateString('en-AU') : '-'}</td>
                            <td className="p-2"><Badge variant={r.source === 'fuel' ? 'warning' : r.source === 'daybook' ? 'warning' : 'info'}>{r.source}</Badge></td>
                            <td className="p-2 uppercase text-xs">{r.expense_type}</td>
                            <td className="p-2 text-right font-bold text-green-600">${(r.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="p-2 text-xs text-slate-600 max-w-[220px] truncate">{r.description || '-'}</td>
                            <td className="p-2 text-xs">{r.trip || r.job_title || (r.machine_name ? `Machine: ${r.machine_name}` : '—')}</td>
                            <td className="p-2 text-xs">
                              {r.needs_reimbursement ? (
                                r.reimbursement_status === 'reimbursed' ? (
                                  <Badge variant="success" data-testid={`reimb-status-${r.id}`}>Reimbursed</Badge>
                                ) : (
                                  <Badge variant="warning" data-testid={`reimb-status-${r.id}`}>Pending</Badge>
                                )
                              ) : (
                                <span className="text-slate-400 text-[11px]">Company card</span>
                              )}
                            </td>
                            <td className="p-2">
                              <Button size="sm" variant="ghost" onClick={() => setPreviewReceipt(r)} data-testid={`preview-my-receipt-${r.id}`}>
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Receipt preview modal */}
              {previewReceipt && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setPreviewReceipt(null)}>
                  <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="my-receipt-preview-modal">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold">{(previewReceipt.expense_type || '').toUpperCase()} — ${(previewReceipt.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
                      <button onClick={() => setPreviewReceipt(null)} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
                    </div>
                    <div className="space-y-2 text-sm mb-3">
                      <div><strong>Date:</strong> {previewReceipt.date ? new Date(previewReceipt.date).toLocaleDateString('en-AU') : '-'}</div>
                      <div><strong>Source:</strong> <Badge variant={previewReceipt.source === 'fuel' ? 'warning' : 'info'}>{previewReceipt.source}</Badge></div>
                      {previewReceipt.description && <div><strong>Description:</strong> {previewReceipt.description}</div>}
                      {previewReceipt.trip && <div><strong>Trip:</strong> {previewReceipt.trip}</div>}
                      {previewReceipt.job_title && <div><strong>Job:</strong> {previewReceipt.job_title}</div>}
                      {previewReceipt.machine_name && <div><strong>Machine:</strong> {previewReceipt.machine_name}</div>}
                      {previewReceipt.fuel_amount_l && <div><strong>Fuel:</strong> {previewReceipt.fuel_amount_l}L · {previewReceipt.payment_type || 'logged'}</div>}
                    </div>
                    {previewReceipt.receipt_url ? (
                      <img src={previewReceipt.receipt_url} alt="Receipt" className="w-full rounded border max-h-[60vh] object-contain bg-slate-50" />
                    ) : (
                      <div className="p-6 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 text-center" data-testid="my-receipt-no-photo">
                        <Receipt className="w-10 h-10 mx-auto text-amber-500 mb-2" />
                        <p className="text-sm font-bold text-amber-900 uppercase">No receipt photo uploaded</p>
                        <p className="text-xs text-amber-700 mt-1">
                          {previewReceipt.no_receipt_reason
                            ? `Reason: ${previewReceipt.no_receipt_reason}`
                            : 'You logged this entry without attaching a receipt photo.'}
                        </p>
                      </div>
                    )}
                  </Card>
                </div>
              )}
            </div>
          )}


          {/* Documents Tab */}
          {activeTab === "documents" && (
            <div className="space-y-4">
              {documents.length > 0 ? (
                documents.map(doc => (
                  <Card key={doc.id}>
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-bold">{doc.document_name || doc.template_name}</h3>
                        <p className="text-sm text-slate-500">Submitted {formatDate(doc.submitted_at)}</p>
                        {doc.job_name && <p className="text-sm text-slate-600">Job: {doc.job_name}</p>}
                      </div>
                      {doc.file_url && (
                        <a 
                          href={doc.file_url} 
                          download
                          className="px-4 py-2 bg-slate-200 text-slate-700 rounded font-semibold text-sm hover:bg-slate-300 transition-colors"
                        >
                          Download
                        </a>
                      )}
                    </div>
                  </Card>
                ))
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <FileImage className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No documents submitted</p>
                </div>
              )}
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === "profile" && profile && (
            <div className="space-y-6" data-testid="profile-tab">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold uppercase">Personal Details</h2>
                <Button
                  onClick={() => editingProfile ? saveProfile() : setEditingProfile(true)}
                  disabled={saving}
                >
                  {saving ? <Spinner size="sm" /> : editingProfile ? 'Save Changes' : 'Edit Profile'}
                </Button>
              </div>

              {editingProfile && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                  <strong>Note:</strong> Your personal information is kept confidential and is only used for payroll and emergency contact purposes.
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                {/* Basic Info */}
                <Card>
                  <h3 className="font-bold uppercase text-sm text-slate-500 mb-4">Basic Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Full Name</label>
                      <p className="font-medium">{profile.first_name} {profile.last_name}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Email</label>
                      <p className="font-medium">{profile.email}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Phone</label>
                      <p className="font-medium">{profile.phone || '-'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Date of Birth</label>
                      {editingProfile ? (
                        <input
                          type="date"
                          value={profileForm.date_of_birth || ''}
                          onChange={(e) => updateProfileField('date_of_birth', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border rounded-md"
                        />
                      ) : (
                        <p className="font-medium">{formatDate(profile.date_of_birth)}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Gender</label>
                      {editingProfile ? (
                        <select
                          value={profileForm.gender || ''}
                          onChange={(e) => updateProfileField('gender', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border rounded-md"
                        >
                          <option value="">Select...</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                          <option value="prefer_not_to_say">Prefer not to say</option>
                        </select>
                      ) : (
                        <p className="font-medium capitalize">{profile.gender?.replace('_', ' ') || '-'}</p>
                      )}
                    </div>
                  </div>
                </Card>

                {/* Job Capabilities */}
                <Card data-testid="capabilities-card">
                  <h3 className="font-bold uppercase text-sm text-slate-500 mb-2">Job Capabilities</h3>
                  <p className="text-xs text-slate-500 mb-4">Tick the jobs you can do on site. This controls which operator/dogman/rigger dropdowns you appear in.</p>
                  <div className="space-y-2">
                    {['operator', 'dogman', 'rigger'].map(cap => {
                      const current = Array.isArray(profileForm.capabilities) ? profileForm.capabilities : (profile.capabilities || []);
                      const on = current.includes(cap);
                      return (
                        <label key={cap} className={`flex items-center gap-3 p-3 border-2 rounded-lg ${editingProfile ? 'cursor-pointer' : 'cursor-default'} ${on ? 'border-orange-500 bg-orange-50' : 'border-slate-200'}`} data-testid={`capability-${cap}`}>
                          <input
                            type="checkbox"
                            className="w-5 h-5"
                            disabled={!editingProfile}
                            checked={on}
                            onChange={() => {
                              const next = on ? current.filter(c => c !== cap) : [...current, cap];
                              updateProfileField('capabilities', next);
                            }}
                          />
                          <div>
                            <p className="font-semibold capitalize">{cap}</p>
                            <p className="text-xs text-slate-500">
                              {cap === 'operator' && 'Can drive/operate machines on a job'}
                              {cap === 'dogman' && 'Can dog a load for an operator'}
                              {cap === 'rigger' && 'Can rig and act as a dogman'}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </Card>

                {/* Address */}
                <Card>
                  <h3 className="font-bold uppercase text-sm text-slate-500 mb-4">Home Address</h3>
                  <div className="space-y-4">
                    {editingProfile ? (
                      <>
                        <input
                          placeholder="Address Line 1"
                          value={profileForm.address_line_1 || ''}
                          onChange={(e) => updateProfileField('address_line_1', e.target.value)}
                          className="w-full px-3 py-2 border rounded-md"
                        />
                        <input
                          placeholder="Address Line 2 (optional)"
                          value={profileForm.address_line_2 || ''}
                          onChange={(e) => updateProfileField('address_line_2', e.target.value)}
                          className="w-full px-3 py-2 border rounded-md"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            placeholder="Suburb"
                            value={profileForm.suburb || ''}
                            onChange={(e) => updateProfileField('suburb', e.target.value)}
                            className="px-3 py-2 border rounded-md"
                          />
                          <select
                            value={profileForm.state || ''}
                            onChange={(e) => updateProfileField('state', e.target.value)}
                            className="px-3 py-2 border rounded-md"
                          >
                            <option value="">State...</option>
                            <option value="NSW">NSW</option>
                            <option value="VIC">VIC</option>
                            <option value="QLD">QLD</option>
                            <option value="WA">WA</option>
                            <option value="SA">SA</option>
                            <option value="TAS">TAS</option>
                            <option value="NT">NT</option>
                            <option value="ACT">ACT</option>
                          </select>
                        </div>
                        <input
                          placeholder="Postcode"
                          value={profileForm.postcode || ''}
                          onChange={(e) => updateProfileField('postcode', e.target.value)}
                          className="w-32 px-3 py-2 border rounded-md"
                        />
                      </>
                    ) : (
                      <>
                        <p className="font-medium">{profile.address_line_1 || '-'}</p>
                        {profile.address_line_2 && <p className="font-medium">{profile.address_line_2}</p>}
                        <p className="font-medium">
                          {profile.suburb}{profile.suburb && profile.state ? ', ' : ''}{profile.state} {profile.postcode}
                        </p>
                      </>
                    )}
                  </div>
                </Card>

                {/* Emergency Contact */}
                <Card>
                  <h3 className="font-bold uppercase text-sm text-slate-500 mb-4 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    Emergency Contact
                  </h3>
                  <div className="space-y-4">
                    {editingProfile ? (
                      <>
                        <input
                          placeholder="Contact Name"
                          value={profileForm.emergency_contact?.name || ''}
                          onChange={(e) => updateNestedField('emergency_contact', 'name', e.target.value)}
                          className="w-full px-3 py-2 border rounded-md"
                        />
                        <input
                          placeholder="Relationship (e.g. Spouse, Parent)"
                          value={profileForm.emergency_contact?.relationship || ''}
                          onChange={(e) => updateNestedField('emergency_contact', 'relationship', e.target.value)}
                          className="w-full px-3 py-2 border rounded-md"
                        />
                        <input
                          placeholder="Phone Number"
                          value={profileForm.emergency_contact?.phone || ''}
                          onChange={(e) => updateNestedField('emergency_contact', 'phone', e.target.value)}
                          className="w-full px-3 py-2 border rounded-md"
                        />
                        <input
                          placeholder="Email (optional)"
                          value={profileForm.emergency_contact?.email || ''}
                          onChange={(e) => updateNestedField('emergency_contact', 'email', e.target.value)}
                          className="w-full px-3 py-2 border rounded-md"
                        />
                      </>
                    ) : (
                      <>
                        <div>
                          <label className="text-xs text-slate-500 uppercase">Name</label>
                          <p className="font-medium">{profile.emergency_contact?.name || '-'}</p>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 uppercase">Relationship</label>
                          <p className="font-medium">{profile.emergency_contact?.relationship || '-'}</p>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 uppercase">Phone</label>
                          <p className="font-medium">{profile.emergency_contact?.phone || '-'}</p>
                        </div>
                      </>
                    )}
                  </div>
                </Card>

                {/* Employment Details */}
                <Card>
                  <h3 className="font-bold uppercase text-sm text-slate-500 mb-4">Employment Details</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Role</label>
                      <p className="font-medium capitalize">{profile.role?.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Position Title</label>
                      {editingProfile ? (
                        <input
                          value={profileForm.position_title || ''}
                          onChange={(e) => updateProfileField('position_title', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border rounded-md"
                        />
                      ) : (
                        <p className="font-medium">{profile.position_title || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Employment Type</label>
                      {editingProfile ? (
                        <select
                          value={profileForm.employment_type || ''}
                          onChange={(e) => updateProfileField('employment_type', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border rounded-md"
                        >
                          <option value="">Select...</option>
                          <option value="full_time">Full Time</option>
                          <option value="part_time">Part Time</option>
                          <option value="casual">Casual</option>
                          <option value="contractor">Contractor</option>
                        </select>
                      ) : (
                        <p className="font-medium capitalize">{profile.employment_type?.replace('_', ' ') || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Start Date</label>
                      {editingProfile ? (
                        <input
                          type="date"
                          value={profileForm.start_date || ''}
                          onChange={(e) => updateProfileField('start_date', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border rounded-md"
                        />
                      ) : (
                        <p className="font-medium">{formatDate(profile.start_date)}</p>
                      )}
                    </div>
                  </div>
                </Card>

                {/* Tax & Super */}
                <Card>
                  <h3 className="font-bold uppercase text-sm text-slate-500 mb-4">Tax & Superannuation</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Tax File Number (TFN)</label>
                      {editingProfile ? (
                        <input
                          type="password"
                          placeholder="Enter TFN"
                          value={profileForm.tax_file_number || ''}
                          onChange={(e) => updateProfileField('tax_file_number', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border rounded-md"
                        />
                      ) : (
                        <p className="font-medium">{profile.tax_file_number ? '••• ••• •••' : 'Not provided'}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Super Fund Name</label>
                      {editingProfile ? (
                        <input
                          value={profileForm.superannuation?.fund_name || ''}
                          onChange={(e) => updateNestedField('superannuation', 'fund_name', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border rounded-md"
                        />
                      ) : (
                        <p className="font-medium">{profile.superannuation?.fund_name || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Member Number</label>
                      {editingProfile ? (
                        <input
                          value={profileForm.superannuation?.member_number || ''}
                          onChange={(e) => updateNestedField('superannuation', 'member_number', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border rounded-md"
                        />
                      ) : (
                        <p className="font-medium">{profile.superannuation?.member_number || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">USI</label>
                      {editingProfile ? (
                        <input
                          placeholder="Unique Super Identifier"
                          value={profileForm.superannuation?.usi || ''}
                          onChange={(e) => updateNestedField('superannuation', 'usi', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border rounded-md"
                        />
                      ) : (
                        <p className="font-medium">{profile.superannuation?.usi || '-'}</p>
                      )}
                    </div>
                  </div>
                </Card>

                {/* Bank Details */}
                <Card>
                  <h3 className="font-bold uppercase text-sm text-slate-500 mb-4">Bank Details (Payroll)</h3>
                  <div className="space-y-4">
                    {editingProfile ? (
                      <>
                        <input
                          placeholder="Bank Name"
                          value={profileForm.bank_details?.bank_name || ''}
                          onChange={(e) => updateNestedField('bank_details', 'bank_name', e.target.value)}
                          className="w-full px-3 py-2 border rounded-md"
                        />
                        <input
                          placeholder="Account Name"
                          value={profileForm.bank_details?.account_name || ''}
                          onChange={(e) => updateNestedField('bank_details', 'account_name', e.target.value)}
                          className="w-full px-3 py-2 border rounded-md"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            placeholder="BSB"
                            value={profileForm.bank_details?.bsb || ''}
                            onChange={(e) => updateNestedField('bank_details', 'bsb', e.target.value)}
                            className="px-3 py-2 border rounded-md"
                          />
                          <input
                            placeholder="Account Number"
                            value={profileForm.bank_details?.account_number || ''}
                            onChange={(e) => updateNestedField('bank_details', 'account_number', e.target.value)}
                            className="px-3 py-2 border rounded-md"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <label className="text-xs text-slate-500 uppercase">Bank</label>
                          <p className="font-medium">{profile.bank_details?.bank_name || '-'}</p>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 uppercase">BSB</label>
                          <p className="font-medium">{profile.bank_details?.bsb || '-'}</p>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 uppercase">Account</label>
                          <p className="font-medium">{profile.bank_details?.account_number ? '•••••' + profile.bank_details.account_number.slice(-4) : '-'}</p>
                        </div>
                      </>
                    )}
                  </div>
                </Card>

                {/* Driver's License */}
                <Card>
                  <h3 className="font-bold uppercase text-sm text-slate-500 mb-4">Driver's License</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs text-slate-500 uppercase">License Number</label>
                      {editingProfile ? (
                        <input
                          value={profileForm.drivers_license_number || ''}
                          onChange={(e) => updateProfileField('drivers_license_number', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border rounded-md"
                        />
                      ) : (
                        <p className="font-medium">{profile.drivers_license_number || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">License Class</label>
                      {editingProfile ? (
                        <input
                          placeholder="e.g. C, LR, MR, HR, HC, MC"
                          value={profileForm.drivers_license_class || ''}
                          onChange={(e) => updateProfileField('drivers_license_class', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border rounded-md"
                        />
                      ) : (
                        <p className="font-medium">{profile.drivers_license_class || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Expiry Date</label>
                      {editingProfile ? (
                        <input
                          type="date"
                          value={profileForm.drivers_license_expiry || ''}
                          onChange={(e) => updateProfileField('drivers_license_expiry', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border rounded-md"
                        />
                      ) : (
                        <p className="font-medium">{formatDate(profile.drivers_license_expiry)}</p>
                      )}
                    </div>
                  </div>
                </Card>

                {/* Medical & Notes */}
                <Card className="md:col-span-2">
                  <h3 className="font-bold uppercase text-sm text-slate-500 mb-4">Medical Conditions & Notes</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Medical Conditions (for site supervisors)</label>
                      {editingProfile ? (
                        <textarea
                          placeholder="Any conditions supervisors should be aware of..."
                          value={profileForm.medical_conditions || ''}
                          onChange={(e) => updateProfileField('medical_conditions', e.target.value)}
                          className="w-full mt-1 px-3 py-2 border rounded-md"
                          rows={2}
                        />
                      ) : (
                        <p className="font-medium">{profile.medical_conditions || 'None specified'}</p>
                      )}
                    </div>
                  </div>
                </Card>

                {/* Certificates */}
                {profile.certificates?.length > 0 && (
                  <Card className="md:col-span-2">
                    <h3 className="font-bold uppercase text-sm text-slate-500 mb-4">Certificates & Tickets</h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {profile.certificates.map(cert => (
                        <div key={cert.id} className="bg-slate-50 p-3 rounded-lg">
                          <p className="font-bold">{cert.name}</p>
                          <p className="text-sm text-slate-500 capitalize">{cert.certificate_type}</p>
                          {cert.expiry_date && (
                            <p className={`text-xs mt-1 ${new Date(cert.expiry_date) < new Date() ? 'text-red-600 font-bold' : 'text-slate-400'}`}>
                              Expires: {formatDate(cert.expiry_date)}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </div>

              {editingProfile && (
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => { setEditingProfile(false); setProfileForm(profile); }}>
                    Cancel
                  </Button>
                  <Button onClick={saveProfile} disabled={saving}>
                    {saving ? <Spinner size="sm" /> : 'Save Changes'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Weekly Times Tab */}
          {activeTab === "weekly" && weeklyTimes && (
            <div className="space-y-6" data-testid="weekly-times-tab">
              {/* Week Navigation */}
              <div className="flex items-center justify-between">
                <Button variant="outline" onClick={() => changeWeek(-1)}>
                  <ChevronLeft className="w-4 h-4 mr-1" /> Previous Week
                </Button>
                <div className="text-center">
                  <p className="font-bold">Week of {formatDate(weeklyTimes.week_start)}</p>
                  <p className="text-sm text-slate-500">{formatHours(weeklyTimes.week_total_hours)} total</p>
                </div>
                <Button variant="outline" onClick={() => changeWeek(1)}>
                  Next Week <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              {/* Info box about alterations */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <strong>Note:</strong> If you forgot to clock in/out or need to change your times, click "Edit" on any entry. 
                Your supervisor will see any changes when reviewing your timesheet.
              </div>

              {/* Reimbursements for the week */}
              {weeklyTimes._receipts && (weeklyTimes._receipts.needs_reimbursement_count > 0 || weeklyTimes._receipts.total > 0) && (
                <Card className="border-2 border-amber-200 bg-amber-50" data-testid="weekly-reimb-card">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-amber-900 flex items-center gap-2"><Receipt className="w-4 h-4" /> This Week's Receipts / Reimbursements</h3>
                    <Button size="sm" variant="outline" onClick={() => setActiveTab("receipts")} data-testid="view-all-receipts-btn">View all</Button>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center mb-2">
                    <div>
                      <p className="text-lg font-bold">{weeklyTimes._receipts.total}</p>
                      <p className="text-[10px] uppercase font-bold text-slate-500">Receipts</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-amber-700">${(weeklyTimes._receipts.needs_reimbursement_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      <p className="text-[10px] uppercase font-bold text-amber-800">Owed to you</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-green-600">${(weeklyTimes._receipts.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      <p className="text-[10px] uppercase font-bold text-slate-500">Total value</p>
                    </div>
                  </div>
                  {weeklyTimes._receipts.needs_reimbursement_count > 0 && (
                    <p className="text-xs text-amber-800">{weeklyTimes._receipts.needs_reimbursement_count} receipt(s) paid on your personal card — will be added to this week's pay.</p>
                  )}
                </Card>
              )}

              {/* Days */}
              {weeklyTimes.days?.length > 0 ? (
                <div className="space-y-4">
                  {weeklyTimes.days.map(day => (
                    <Card key={day.date}>
                      <div className="flex justify-between items-center mb-3">
                        <div>
                          <h3 className="font-bold">{day.day_name}</h3>
                          <p className="text-sm text-slate-500">{formatDate(day.date)}</p>
                        </div>
                        <Badge className="text-lg px-3">{formatHours(day.total_hours)}</Badge>
                      </div>
                      <div className="space-y-2">
                        {day.entries.map(entry => (
                          <div 
                            key={entry.id} 
                            className={`flex flex-col md:flex-row md:justify-between md:items-center p-3 rounded-lg gap-2 ${
                              entry.alteration_applied ? 'bg-amber-50 border border-amber-200' : 
                              entry.has_pending_alteration ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50'
                            }`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium">{entry.job_name || 'Unknown Job'}</p>
                                {entry.alteration_applied && (
                                  <Badge variant="warning">Edited</Badge>
                                )}
                                {entry.has_pending_alteration && (
                                  <Badge variant="info">Pending Approval</Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-600">
                                <span className="font-medium">{formatTime(entry.clock_in)}</span>
                                {' - '}
                                <span className="font-medium">{entry.clock_out ? formatTime(entry.clock_out) : 'Active'}</span>
                                <span className="ml-3 text-orange-600 font-bold">({formatHours(entry.hours)})</span>
                              </p>
                              {entry.alteration_reason && (
                                <p className="text-xs text-amber-600 mt-1">Change reason: {entry.alteration_reason}</p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              {entry.clock_out && !entry.has_pending_alteration && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedTimeEntry(entry);
                                    setAlterationForm({
                                      reason: '',
                                      signature_data: '',
                                      requested_clock_in: entry.clock_in?.split('T')[1]?.substring(0, 5),
                                      requested_clock_out: entry.clock_out?.split('T')[1]?.substring(0, 5)
                                    });
                                    setShowAlterationModal(true);
                                  }}
                                >
                                  <Edit2 className="w-3 h-3 mr-1" />
                                  Edit Times
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No time entries for this week</p>
                </div>
              )}

              {/* Pending Alterations Summary */}
              {profile?.pending_alterations?.length > 0 && (
                <Card className="border-amber-200 bg-amber-50">
                  <h3 className="font-bold uppercase mb-4 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    Pending Time Change Requests
                  </h3>
                  <div className="space-y-2">
                    {profile.pending_alterations.map(alt => (
                      <div key={alt.id} className="flex justify-between items-center p-3 bg-white rounded-lg border">
                        <div>
                          <p className="font-medium">Change for {formatDate(alt.original_clock_in)}</p>
                          <p className="text-sm text-slate-600">{alt.reason}</p>
                          <p className="text-xs text-slate-400 mt-1">Waiting for supervisor approval</p>
                        </div>
                        <Badge variant="warning">Pending</Badge>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Time Alteration Modal - Simplified */}
          {showAlterationModal && selectedTimeEntry && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-md">
                <h2 className="text-xl font-bold uppercase mb-2">Edit Time Entry</h2>
                <p className="text-sm text-slate-600 mb-4">
                  Change your clock times. Your supervisor will see this change when reviewing your timesheet.
                </p>
                
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <label className="text-xs text-slate-500 uppercase">Original Times</label>
                    <p className="font-bold text-lg">
                      {formatTime(selectedTimeEntry.clock_in)} - {formatTime(selectedTimeEntry.clock_out)}
                    </p>
                    <p className="text-sm text-slate-500">{selectedTimeEntry.job_name}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-slate-500 uppercase">New Clock In</label>
                      <input
                        type="time"
                        value={alterationForm.requested_clock_in || ''}
                        onChange={(e) => setAlterationForm(prev => ({ ...prev, requested_clock_in: e.target.value }))}
                        className="w-full mt-1 px-3 py-2 border-2 rounded-md text-lg font-medium"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">New Clock Out</label>
                      <input
                        type="time"
                        value={alterationForm.requested_clock_out || ''}
                        onChange={(e) => setAlterationForm(prev => ({ ...prev, requested_clock_out: e.target.value }))}
                        className="w-full mt-1 px-3 py-2 border-2 rounded-md text-lg font-medium"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs text-slate-500 uppercase">Reason for Change *</label>
                    <textarea
                      value={alterationForm.reason}
                      onChange={(e) => setAlterationForm(prev => ({ ...prev, reason: e.target.value }))}
                      placeholder="e.g. Forgot to clock in, was on site from 6am..."
                      className="w-full mt-1 px-3 py-2 border-2 rounded-md"
                      rows={2}
                    />
                    <p className="text-xs text-slate-400 mt-1">This will be shown to your supervisor</p>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-6">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      setShowAlterationModal(false);
                      setSelectedTimeEntry(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1"
                    onClick={submitTimeAlteration}
                    disabled={saving || !alterationForm.reason}
                  >
                    {saving ? <Spinner size="sm" /> : 'Submit Change'}
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MyRecordsPage;
